import { alpha, darken, getLuminance, Theme } from "@mui/material/styles";

/**
 * Canvas + card treatment for the public (unauthenticated) pages (NG-8606).
 *
 * NG-8572 left every public form floating bare on a flat page background. The design system asks for
 * the industry-standard shape instead: a card holding the form, sitting on a canvas that is visibly
 * not the card.
 *
 * That is harder than it looks here, because `background.paper` and `background.default` are the SAME
 * colour in the two cases that matter:
 *
 *   - the light app theme          — both `#fff` (defaultPalette in muiUtils.ts)
 *   - every tenant branding config — `buildBrandingTheme` maps both channels from the single
 *                                    `formBackground` slot (brandingTheme.ts)
 *
 * Only the unbranded dark theme separates them on its own (`#141920` canvas / `#1D2530` paper). So a
 * naive "card = paper on canvas = default" is invisible in light mode and under every branded tenant.
 *
 * Hence: keep `background.default` as the canvas when the theme already separates the two, and derive
 * a tint from the card colour when it does not. Deriving the CANVAS rather than lightening the card is
 * what works in practice — a tenant's formBackground is very often pure white, where lightening the
 * card is a no-op, while darkening the canvas always yields separation, and it also gives the
 * conventional dark-mode reading of a card lifted above a deeper page.
 *
 * The card additionally always carries a 1px border and a shadow, so it still reads even against a
 * canvas it cannot separate from (a tenant who sets formBackground to pure black or pure white gets
 * no useful derivation, and `darken` cannot go below black).
 */

/** Dark surfaces need a bigger nudge than light ones to read as a separate layer. */
const CANVAS_SHIFT_ON_LIGHT = 0.05;
const CANVAS_SHIFT_ON_DARK = 0.35;

export const publicPageCanvas = (theme: Theme): string => {
    const { paper, default: pageDefault } = theme.palette.background;

    if (pageDefault !== paper) {
        // The theme already provides separation (unbranded dark mode) — respect it, and keep the
        // branded `formBackground` as the literal page colour wherever it can be.
        return pageDefault;
    }

    return darken(paper, getLuminance(paper) < 0.5 ? CANVAS_SHIFT_ON_DARK : CANVAS_SHIFT_ON_LIGHT);
};

/**
 * The card surface itself. Spread into a styled/sx block.
 *
 * `boxSizing` is not optional: no CssBaseline/border-box reset is applied app-wide (see the note in
 * publicFormLayout.ts), so under the default content-box the padding is added ON TOP of `width: 100%`
 * and the card overflows its column.
 */
export const publicPageCard = (theme: Theme) =>
    ({
        backgroundColor: theme.palette.background.paper,
        // Branding routes its `inputBorder` slot through `divider`, so this follows a tenant config.
        border: `1px solid ${theme.palette.divider}`,
        // A string, not 16: this object is spread into `sx`, where a unitless borderRadius is a
        // multiplier on theme.shape.borderRadius (4) and would render a 64px pill.
        borderRadius: "16px", // design system radius-lg
        padding: theme.spacing(4, 3), // 32 / 24 on the 8px grid
        boxSizing: "border-box" as const,
        // Design system elevation level 1. Shadows stay black in both colour modes.
        boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.18)}`,
    }) as const;
